// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export const GLOSSARY: Readonly<Record<string, { title: string; body: string }>> = {
  'silent-drop': {
    title: 'Silently dropped',
    body: 'The keyword is accepted, no error is returned, and the constraint simply does not exist any more. It is the worst of the three outcomes: a rejection tells you at once, an accepted-and-enforced constraint does its job, and a silent drop leaves you believing a guarantee you no longer have.',
  },
  strict: {
    title: 'Strict mode',
    body: 'With strict on, the model is constrained during decoding so it cannot physically emit a value outside the schema. With it off, the schema is a suggestion in the prompt. The catch is that a schema can be silently downgraded to best-effort when it cannot be compiled, and the only signal is `strict: false` coming back on the tool.',
  },
  gbnf: {
    title: 'GBNF',
    body: "llama.cpp's grammar format. A grammar constrains sampling token by token, which is why it can enforce shape but not, say, uniqueness — a context-free grammar has no memory of what it already emitted.",
  },
  namespace: {
    title: 'Namespace group',
    body: 'A 2026 Responses tool entry of `type: "namespace"` that carries its own nested tools. The API validates inside it — rejections come back on paths like `tools[9].tools[4].parameters` — so a linter that only walks the top level of the array can miss everything.',
  },
  provenance: {
    title: 'Provenance',
    body: 'Where a rule\'s authority comes from. DOC is quotable from the vendor\'s own documentation. SDK was read out of the vendor\'s own SDK source, which is where several undocumented rules actually live. SRC was read from llama.cpp or xgrammar source. FIELD is a third-party capture of a real 400 — someone else\'s error message, not ours.',
  },
  'key-order': {
    title: 'Key order',
    body: 'A grammar fixes the order of object keys, and the order it fixes is the declaration order of `properties` — not the order of your `required` array. Anthropic, separately, reorders keys so required ones come first. OpenAI preserves your order. Gemini needs a non-standard `propertyOrdering` array. Four different answers to the same question.',
  },
  aggregate: {
    title: 'Request-wide, not per-tool',
    body: "Anthropic's strict limits count across the whole request, not per tool. Four strict tools with six optional parameters each already reaches the 24-parameter limit, though no single tool looks remotely complex.",
  },
  lossy: {
    title: 'Lossy',
    body: 'A constraint that could not be carried across. Toolwright separates four kinds: dropped (gone, harmlessly), rejected (would have caused a 400), inert (accepted and documented to do nothing), and semantics-changed — the only one that alters what your schema means, and the only one that gets a warning rather than a table row.',
  },
};

/**
 * Click-to-define tooltips.
 *
 * Dismissal follows the same contract as every other overlay: Escape, a pointer anywhere outside,
 * and re-clicking the term itself. One event family only.
 */
export function initGlossary(tooltip: HTMLElement): void {
  let current: HTMLElement | null = null;

  const hide = (): void => {
    tooltip.setAttribute('hidden', '');
    current = null;
  };

  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link[data-term]');
    if (!target) {
      if (current && !tooltip.contains(e.target as Node)) hide();
      return;
    }
    const term = target.dataset['term'];
    const entry = term ? GLOSSARY[term] : undefined;
    if (!entry) return;
    if (current === target) {
      hide();
      return;
    }
    e.preventDefault();
    tooltip.replaceChildren();
    const h = document.createElement('strong');
    h.textContent = entry.title;
    tooltip.append(h, document.createTextNode(entry.body));
    tooltip.removeAttribute('hidden');
    const r = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
    const below = r.bottom + 8;
    tooltip.style.top =
      below + tooltip.offsetHeight > window.innerHeight
        ? `${Math.max(12, r.top - tooltip.offsetHeight - 8)}px`
        : `${below}px`;
    current = target;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) hide();
  });
  window.addEventListener('resize', hide);
}
